<!-- =========================================================================================
  File Name: DataListListView.vue
  Description: Data List - List View
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div id="company-list-list-view" class="companies-page">
    <div class="vx-card p-6">
      <!-- Header: title + search -->
      <div class="companies-header">
        <h4 class="companies-title">Companies</h4>
        <div class="companies-search">
          <feather-icon icon="SearchIcon" svgClasses="w-4 h-4 text-grey" />
          <input
            type="text"
            v-model="searchQuery"
            class="companies-search__input"
            placeholder="Search name, ID or telephone…"
          />
          <feather-icon
            v-if="searchQuery"
            icon="XIcon"
            svgClasses="w-4 h-4 text-grey cursor-pointer"
            @click="searchQuery = ''"
          />
        </div>
      </div>

      <vs-table ref="table" stripe pagination :max-items="itemsPerPage" :data="filteredCompanies">
        <template slot="thead">
          <vs-th>Company</vs-th>
          <vs-th>TaxiPoint ID</vs-th>
          <vs-th>Ghost Company ID</vs-th>
          <vs-th sort-key="wallet_amount">Wallet Amount</vs-th>
          <vs-th sort-key="charity_collected">Charity Collected</vs-th>
          <vs-th>Telephone</vs-th>
          <vs-th>Configuration</vs-th>
        </template>

        <template slot-scope="{ data }">
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <!-- Company: avatar + name + update-logo -->
            <vs-td>
              <div class="company-cell">
                <div class="company-cell__avatar">
                  <img
                    v-if="tr.logo_url"
                    :src="imageRoute + '/' + tr.logo_url"
                    class="company-cell__logo"
                  />
                  <span
                    v-else
                    class="company-cell__initials"
                    :style="{ background: avatarColor(tr.name) }"
                  >{{ initials(tr.name) }}</span>
                </div>
                <div class="company-cell__meta">
                  <span class="company-cell__name font-medium">{{ tr.name }}</span>
                  <input
                    type="file"
                    class="hidden"
                    :ref="'updateImgInput' + tr.id"
                    accept="image/*"
                    :id="tr.id"
                    @change="handleClick"
                  />
                  <button
                    type="button"
                    class="company-cell__logo-btn"
                    @click="updateImageClick('updateImgInput' + tr.id)"
                  >
                    <feather-icon icon="CameraIcon" svgClasses="w-3 h-3" />
                    <span>Update logo</span>
                  </button>
                </div>
              </div>
            </vs-td>

            <!-- TaxiPoint ID -->
            <vs-td>
              <span class="id-chip">{{ tr.id }}</span>
            </vs-td>

            <!-- Ghost Company ID -->
            <vs-td>
              <span class="id-chip id-chip--muted">{{ tr.api_company_id || '—' }}</span>
            </vs-td>

            <!-- Wallet -->
            <vs-td>
              <span class="font-medium">&pound;&nbsp;{{ formatMoney(tr.wallet_amount) }}</span>
            </vs-td>

            <!-- Charity -->
            <vs-td>
              <div class="flex items-center" style="grid-gap: 6px">
                <span class="font-medium">&pound;&nbsp;{{ formatMoney(tr.charity_collected) }}</span>
                <vs-button
                  size="small"
                  radius
                  type="flat"
                  color="dark"
                  icon="icon-download"
                  icon-pack="feather"
                  @click="setCompanyForCharity(tr.id)"
                />
              </div>
            </vs-td>

            <!-- Telephone -->
            <vs-td>
              <span class="font-medium">{{ tr.telephone || '—' }}</span>
            </vs-td>

            <!-- Configuration -->
            <vs-td>
              <div class="flex" style="grid-gap: 6px">
                <vs-button
                  @click="setSelectedCompany(indextr, tr)"
                  size="small"
                  color="warning"
                  type="filled"
                  icon-pack="feather"
                  icon="icon-settings"
                  >Settings</vs-button
                >
                <vs-button
                  @click="setSelectedCompanyForCharity(indextr, tr)"
                  size="small"
                  color="success"
                  type="filled"
                  icon-pack="feather"
                  icon="icon-gift"
                  >Charity</vs-button
                >
                <vs-button
                  @click="openBanners(tr)"
                  size="small"
                  color="primary"
                  type="filled"
                  icon-pack="feather"
                  icon="icon-image"
                  >Banners</vs-button
                >
              </div>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </div>

    <!-- ============ Company Settings prompt ============ -->
    <vs-prompt
      title="Company Settings"
      @accept="acceptAlert"
      accept-text="Save"
      :active.sync="companySettingPrompt"
    >
      <h5 class="text-primary mb-3 flex justify-center">
        Enter capabilities and their booking fee
      </h5>
      <div
        v-for="(field, key) in capability_fields"
        :key="key"
        class="mb-2 input-group input-group"
      >
        <div class="flex items-start" style="grid-gap: 3px">
          <vs-input
            label-placeholder="Capability"
            v-model="field.key"
            :disabled="field.key == 'default'"
          />

          <vs-input
            label-placeholder="Booking fee"
            type="text"
            v-model="field.value"
          />
          <vs-button
            class="mt-5"
            color="success"
            radius
            icon="add"
            type="filled"
            @click="AddField"
          >
          </vs-button>

          <vs-button
            class="mt-5"
            :disabled="field.key == 'default'"
            color="danger"
            radius
            icon="cancel"
            type="filled"
            @click="RemoveField(key)"
          ></vs-button>
        </div>
      </div>
    </vs-prompt>

    <!-- ============ Charity Details prompt ============ -->
    <vs-prompt
      title="Charity Details"
      @accept="saveCharity"
      accept-text="Save"
      :active.sync="charityPrompt"
    >
      <div class="flex justify-between">
        <h5 class="text-primary mb-2">
          Status
        </h5>
        <vs-switch color="success" v-model="charityDetails.is_enabled">
          <span slot="on">Enabled</span>
          <span slot="off">Disabled</span>
        </vs-switch>
      </div>
      <div>
        <label class="text-sm text-primary">Title</label>
        <vs-input
          class="w-full mb-2"
          placeholder="Title"
          v-model="charityDetails.title"
        />

        <label class="text-sm text-primary mt-9">Description</label>
        <vs-textarea
          class="w-full "
          height="200px"
          placeholder="Enter description here...."
          v-model="charityDetails.description"
        />
      </div>
    </vs-prompt>

    <!-- ============ Charity Excel prompt ============ -->
    <vs-prompt
      title="Charity Details"
      :active.sync="charityExcelPopup"
      :acceptText="'Download'"
      @accept="downloadExcel"
    >
      <div class="con-exemple-prompt">
        <label class="text-sm font-bold ml-1">Start date: *</label>
        <datepicker
          name="start-date"
          class="mb-4"
          label="Name"
          placeholder="From Date"
          v-model="startDate"
        >
        </datepicker>
        <label class="text-sm font-bold ml-1">End date: *</label>
        <datepicker
          :calendar-button="true"
          :bootstrap-styling="true"
          icon-pack="fa"
          val-icon-pack="fa"
          calendar-button-icon="eye"
          name="end-date"
          class="mb-4"
          label="Name"
          placeholder="To Date"
          v-model="endDate"
          icon-after="true"
          :disabled="!startDate"
        >
        </datepicker>
      </div>
    </vs-prompt>

    <!-- ============ Company Banner Images (max 6) ============ -->
    <vs-popup
      class="banner-popup"
      :title="bannerCompany ? 'Banner Images — ' + bannerCompany.name : 'Banner Images'"
      :active.sync="bannerPopup"
    >
      <div class="mb-3 flex items-center justify-between">
        <h6 class="text-primary">
          {{ banners.length }} / {{ maxBanners }} images
        </h6>
        <input
          type="file"
          class="hidden"
          ref="bannerFileInput"
          accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
          @change="handleBannerFile"
        />
        <vs-button
          size="small"
          color="primary"
          type="filled"
          icon-pack="feather"
          icon="icon-upload"
          :disabled="banners.length >= maxBanners"
          @click="triggerBannerUpload"
          >Add image</vs-button
        >
      </div>

      <p v-if="banners.length >= maxBanners" class="text-sm text-warning mb-3">
        Maximum of {{ maxBanners }} banner images reached. Delete one to add another.
      </p>
      <p v-else class="text-sm text-grey mb-3">
        PNG, JPG, JPEG or WEBP. Max 2 MB per image.
      </p>

      <div v-if="banners.length" class="banner-grid">
        <div
          v-for="banner in banners"
          :key="banner.id"
          class="banner-item"
        >
          <img
            :src="bannerImageUrl(banner)"
            class="banner-thumb"
          />
          <vs-button
            class="banner-delete"
            size="small"
            color="danger"
            type="filled"
            icon-pack="feather"
            icon="icon-trash-2"
            radius
            @click="deleteBanner(banner)"
          />
        </div>
      </div>

      <div v-else class="flex justify-center py-6">
        <p class="text-sm">No banner images yet. Click “Add image” to upload.</p>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import { MAIN_DOMAIN } from "@/environment";
import moment from "moment";
  import Datepicker from "vuejs-datepicker";

import {
  _list,
  _upload_logo,
  _update_settings,
  _update_charity,
  _charity_details,
  _list_banners,
  _upload_banner,
  _delete_banner
} from "@/services/Company";

export default {
  components: {
    Datepicker
  },
  watch: {
    startDate() {
      this.endDate = ''
    }
  },
  computed: {
    validForm() {
      return (
        this.startDate != "" &&
        this.endDate != "" &&
        Date.parse(this.endDate) - Date.parse(this.startDate) >= 0 &&
        !this.errors.has("event-url")
      );
    },
    currentPage() {
      if (this.isMounted) {
        return this.$refs.table.currentx;
      }
      return 0;
    },
    queriedItems() {
      return this.$refs.table
        ? this.$refs.table.queriedResults.length
        : this.companies.length;
    },
    filteredCompanies() {
      const q = (this.searchQuery || "").trim().toLowerCase();
      if (!q) return this.companies;
      return this.companies.filter(c => {
        return (
          String(c.name || "").toLowerCase().includes(q) ||
          String(c.id || "").toLowerCase().includes(q) ||
          String(c.api_company_id || "").toLowerCase().includes(q) ||
          String(c.telephone || "").toLowerCase().includes(q)
        );
      });
    }
  },
  data() {
    return {
      searchQuery: "",
      charityExcelPopup: false,
      startDate: "",
      endDate: "",
      companyId: -1,

      selected: [],
      // companies: [],
      itemsPerPage: 10,
      isMounted: false,

      // Data Sidebar
      addNewDataSidebar: false,
      sidebarData: {},
      companies: [],
      imageRoute: MAIN_DOMAIN + "/uploads",
      companySettingPrompt: false,
      currentIdx: -1,
      currentCompany: null,
      capability_fields: [{}],
      charityPrompt: false,
      enableCharity: false,
      charityDetails: {
        title: null,
        description: null,
        is_enabled: null
      },
      // Company banner images
      bannerPopup: false,
      bannerCompany: null,
      banners: [],
      maxBanners: 6,
      bannerLoading: false
    };
  },
  methods: {
    // ---- Display helpers (redesign) ----
    formatMoney(value) {
      const n = parseFloat(value) || 0;
      return n.toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    initials(name) {
      if (!name) return "?";
      const parts = String(name).trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return "?";
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },
    avatarColor(name) {
      const palette = [
        "#7367F0", "#28C76F", "#FF9F43", "#EA5455",
        "#00CFE8", "#9C27B0", "#5A8DEE", "#F8617F"
      ];
      const str = String(name || "");
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      return palette[Math.abs(hash) % palette.length];
    },
    updateImageClick(e) {
      this.$refs[e][0].click();
    },
    // ---- Company banner images (max 6) ----
    openBanners(company) {
      this.bannerCompany = company;
      this.banners = [];
      this.bannerPopup = true;
      this.loadBanners();
    },
    applyBannerList(obj) {
      const payload = obj || {};
      const list = payload.banners || payload.list || [];
      this.banners = list.slice().sort(function(a, b) {
        return (a.sort_order || 0) - (b.sort_order || 0);
      });
      if (payload.max) this.maxBanners = payload.max;
    },
    loadBanners() {
      if (!this.bannerCompany) return;
      this.$vs.loading();
      _list_banners(this.bannerCompany.id)
        .then(response => {
          this.$vs.loading.close();
          this.applyBannerList(response.data && response.data.obj);
        })
        .catch(error => {
          this.$vs.loading.close();
          this.$vs.notify({
            title: "Failed to load banners",
            text: (error.response && error.response.data && error.response.data.responseMessage) || error.message,
            iconPack: "feather",
            icon: "icon-cross-circle",
            color: "danger"
          });
        });
    },
    bannerImageUrl(banner) {
      // Prefer a full URL from the API; otherwise build it from the uploads route.
      var path = banner.image_url || banner.url || banner.image || banner.filename || banner.path;
      if (!path) return "";
      if (/^https?:\/\//.test(path)) return path;
      return this.imageRoute + "/" + path;
    },
    triggerBannerUpload() {
      if (this.banners.length >= this.maxBanners) {
        this.$vs.notify({
          title: "Limit reached",
          text: "You can add up to " + this.maxBanners + " banner images.",
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning"
        });
        return;
      }
      this.$refs.bannerFileInput.click();
    },
    handleBannerFile(e) {
      const files = e.target.files;
      const rawFile = files[0];
      if (!rawFile) return;
      this.uploadBanner(rawFile);
      e.target.value = null; // allow re-selecting the same file
    },
    bannerValidationError(rawFile) {
      const allowedExt = ["png", "jpg", "jpeg", "webp"];
      const allowedMime = ["image/png", "image/jpeg", "image/webp"];
      const maxBytes = 2048 * 1024; // Laravel max:2048 (KB)
      const name = (rawFile.name || "").toLowerCase();
      const ext = name.includes(".") ? name.split(".").pop() : "";
      const typeOk = allowedMime.indexOf(rawFile.type) !== -1 || allowedExt.indexOf(ext) !== -1;
      if (!typeOk) {
        return "Banner must be a PNG, JPG, JPEG, or WEBP file.";
      }
      if (rawFile.size > maxBytes) {
        return "Banner must be 2 MB or smaller.";
      }
      return null;
    },
    bannerUploadErrorText(error) {
      const data = error.response && error.response.data;
      if (!data) return error.message;
      if (data.errors && data.errors.banner && data.errors.banner.length) {
        return data.errors.banner.join(" ");
      }
      return data.responseMessage || data.message || error.message;
    },
    uploadBanner(rawFile) {
      if (!this.bannerCompany) return;
      if (this.banners.length >= this.maxBanners) return;
      const validationError = this.bannerValidationError(rawFile);
      if (validationError) {
        this.$vs.notify({
          title: "Invalid banner",
          text: validationError,
          iconPack: "feather",
          icon: "icon-alert-circle",
          color: "warning"
        });
        return;
      }
      const formData = new FormData();
      formData.append("banner", rawFile);
      this.bannerLoading = true;
      this.$vs.loading();
      _upload_banner(this.bannerCompany.id, formData)
        .then(response => {
            this.$vs.loading.close();
            this.bannerLoading = false;
            const obj = (response.data && response.data.obj) || {};
            if (obj.banner) {
              this.banners.push(obj.banner);
            } else if (obj.banners) {
              this.applyBannerList(obj);
            } else {
              this.loadBanners();
            }
            this.$vs.notify({
              title: response.data.responseTitle || "Success",
              text: response.data.responseMessage || "Banner uploaded successfully",
              iconPack: "feather",
              icon: "icon-check-circle",
              color: "success"
            });
          },
          error => {
            this.$vs.loading.close();
            this.bannerLoading = false;
            this.$vs.notify({
              title: (error.response && error.response.data && error.response.data.responseTitle) || "Upload failed",
              text: this.bannerUploadErrorText(error),
              iconPack: "feather",
              icon: "icon-cross-circle",
              color: "danger"
            });
          }
        )
        .catch(error => {
          this.$vs.loading.close();
          this.bannerLoading = false;
          this.$vs.notify({
            text: error.message,
            iconPack: "feather",
            icon: "icon-cross-circle",
            color: "danger"
          });
        });
    },
    deleteBanner(banner) {
      if (!this.bannerCompany) return;
      this.$vs.loading();
      _delete_banner(this.bannerCompany.id, banner.id)
        .then(
          response => {
            this.$vs.loading.close();
            this.banners = this.banners.filter(b => b.id !== banner.id);
            this.$vs.notify({
              title: (response.data && response.data.responseTitle) || "Deleted",
              text: (response.data && response.data.responseMessage) || "Banner deleted successfully",
              iconPack: "feather",
              icon: "icon-check-circle",
              color: "success"
            });
          },
          error => {
            this.$vs.loading.close();
            this.$vs.notify({
              title: (error.response && error.response.data && error.response.data.responseTitle) || "Delete failed",
              text: (error.response && error.response.data && error.response.data.responseMessage) || error.message,
              iconPack: "feather",
              icon: "icon-cross-circle",
              color: "danger"
            });
          }
        )
        .catch(error => {
          this.$vs.loading.close();
          this.$vs.notify({
            text: error.message,
            iconPack: "feather",
            icon: "icon-cross-circle",
            color: "danger"
          });
        });
    },
    AddField() {
      this.$emit("dataFeature", this.capability_fields);
      this.capability_fields.push({ key: null, value: null });
    },
    RemoveField(key) {
      this.capability_fields.splice(key, 1);
    },
    updateCurrImg(input) {
      if (input.target.files && input.target.files[0]) {
        var reader = new FileReader();
        reader.onload = e => {
          console.log(e.target.result);
        };
        reader.readAsDataURL(input.target.files[0]);
      }
    },
    setSelectedCompany(idx, val) {
      console.log(idx);
      console.log(val);
      this.currentCompany = val;
      this.currentIdx = idx;
      this.companySettingPrompt = true;
      this.capability_fields = JSON.parse(JSON.stringify(val.settings));
    },

    setSelectedCompanyForCharity(idx, val) {
      this.currentCompany = val;
      this.currentIdx = idx;
      this.charityPrompt = true;
      if (val.charity_detail) {
        this.charityDetails = val.charity_detail;
      } else {
        this.charityDetails = {
          title: null,
          description: null,
          is_enabled: 0
        };
      }
    },
    setCompanyForCharity(id)
    {
      this.companyId = id
      this.charityExcelPopup = true
    },

    acceptAlert() {
      var settings = this.capability_fields;
      var realSettings = settings.filter(function(e) {
        return e.key != null && e.value != null;
      });
      console.log(this.companies[this.currentIdx]);
      _update_settings(this.currentCompany.id, {
        settings: realSettings
      })
        .then(res => {

          console.log(this.currentIdx);
          this.companies[this.currentIdx] = res.data.obj.company;

      console.log(this.companies[this.currentIdx]);

          this.$vs.notify({
            color: "success",
            title: "Success",
            text: "Booking fee updated successfully"
          });
        })
        .catch(() => {});
    },
    saveCharity() {
      _update_charity(this.currentCompany.id, this.charityDetails)
        .then(
          res => {
            this.companies[this.currentIdx] = res.data.obj.company;
            this.$vs.notify({
              color: "success",
              title: "Success",
              text: "Company charity updated successfully"
            });
          },
          error => {
            this.$vs.notify({
              color: "danger",
              title: "failed operation",
              text: error.message
            });
          }
        )
        .catch(err => {
          this.$vs.notify({
            color: "danger",
            title: "Catch failed",
            text: err.message
          });
        });
    },
    handleClick(e) {
      const files = e.target.files;
      const rawFile = files[0];
      if (!rawFile) return;
      this.uploadFile(e.target.id, rawFile);
    },
    uploadFile(company_id, rawFile) {
      let formData = new FormData();
      formData.append("photo", rawFile);
      this.$vs.loading();
      _upload_logo(company_id, formData)
        .then(
          response => {
            this.$vs.loading.close();
            let obj = this.companies.find(obj => obj.id == company_id);

            obj.logo_url = response.data.obj.filename;
            this.$vs.notify({
              title: response.data.responseTitle,
              text: response.data.responseMessage,
              iconPack: "feather",
              icon: "icon-check-circle",
              color: "success"
            });
          },
          error => {
            this.$vs.loading.close();
            this.$vs.notify({
              title: error.response.data.responseTitle,
              text: error.response.data.responseMessage,
              iconPack: "feather",
              icon: "icon-cross-circle",
              color: "danger"
            });
          }
        )
        .catch(error => {
          this.$vs.loading.close();
          this.$vs.notify({
            text: error.message,
            iconPack: "feather",
            icon: "icon-cross-circle",
            color: "danger"
          });
        });
      this.$refs["fileInput"].value = null; // fix can't select the same excel
    },
     downloadExcel() {
      this.$vs.loading();

      let df = moment(this.startDate).format('YYYY-MM-DD');
      let dt = moment(this.endDate).format('YYYY-MM-DD');
      const data = {
        from_date: df,
        to_date: dt
      };

      _charity_details(this.companyId, data).then((res) => {
        this.clearFields();
            import("@/vendor/Export2Excel").then((excel) => {
            this.$vs.loading.close();

            const list = res.data.obj.data;
            const headerVal = [
                  "Booking ID",
                  "Customer Name",
                  "Charity Amount",
                  "Paid on"
            ];
            const data = this.formatJson(headerVal, list);
            excel.export_json_to_excel({
              header: [
                  "Booking ID",
                  "Customer Name",
                  "Charity Amount",
                  "Paid on"
            ],
              data,
              filename: moment(),
              autoWidth: true,
              bookType: "xls",
            });

          });
      });
    },
    formatJson(filterVal, jsonData) {
      return jsonData.map((v) =>
        filterVal.map((j) => {
          if (j == "Booking ID") return v.api_booking_id;
          else if (j == "Customer Name") return v.customer_name;
          else if (j == "Charity Amount") return v.amount+ " £ ";
          else if (j == "Paid on") return v.created_at;
          else return v[j];
        })
      );
    },
    clearFields() {
      this.startDate = this.endDate  = "";
    },
  },
  created() {
    this.$vs.loading();
    _list()
      .then(response => {
        this.$vs.loading.close();
        this.companies = response.data.obj.list;
      })
      .catch(error => {
        this.$vs.loading.close();
        this.$vs.notify({
          title: error.response.data.responseTitle,
          text: error.response.data.responseMessage,
          iconPack: "feather",
          icon: "icon-cross-circle",
          color: "danger"
        });
      });
  },
  mounted() {
    this.isMounted = true;
    //fire.$on('reset', this.resetField);
  }
};
</script>

<style lang="scss">
/* =====================================================================
   Companies page — matched to the Vuexy (vuesax) theme.
   Uses the stock striped vs-table like users/payments/services pages,
   with only light, on-theme polish (header search, avatar, id chips).
   ===================================================================== */

.companies-page {
  .companies-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.25rem;
  }
  .companies-title {
    margin: 0;
    font-weight: 600;
    color: #2c2c40;
  }
  .companies-search {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: #f8f8fc;
    border: 1px solid #e6e6f2;
    border-radius: 0.5rem;
    padding: 0.5rem 0.85rem;
    min-width: 300px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus-within {
      border-color: rgba(115, 103, 240, 0.6);
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
    }
    &__input {
      border: none;
      outline: none;
      background: transparent;
      flex: 1;
      font-size: 0.9rem;
      color: #2c2c40;

      &::placeholder { color: #b6b6c8; }
    }
  }

  /* Company cell: small avatar + name (subtle, on-theme) */
  .company-cell {
    display: flex;
    align-items: center;
    gap: 0.7rem;

    &__avatar {
      width: 38px;
      height: 38px;
      border-radius: 0.5rem;
      overflow: hidden;
      flex-shrink: 0;
      background: #f4f4fb;
    }
    &__logo {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    &__initials {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-weight: 600;
      font-size: 0.85rem;
    }
    &__meta {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
      min-width: 0;
    }
    &__name {
      color: #2c2c40;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 16rem;
    }
    &__logo-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      width: fit-content;
      padding: 0;
      background: transparent;
      border: none;
      cursor: pointer;
      color: #7367f0;
      font-size: 0.75rem;
      font-weight: 500;
      opacity: 0.85;
      transition: opacity 0.15s ease;

      &:hover { opacity: 1; text-decoration: underline; }
    }
  }

  /* Identifier chips */
  .id-chip {
    display: inline-block;
    font-family: "SFMono-Regular", "Consolas", "Menlo", monospace;
    font-size: 0.82rem;
    color: #4a4a63;
    background: rgba(115, 103, 240, 0.08);
    padding: 0.15rem 0.5rem;
    border-radius: 0.35rem;

    &--muted {
      color: #8a8aa0;
      background: rgba(138, 138, 160, 0.1);
    }
  }
}

/* Banner popup grid (kept) */
.banner-popup .banner-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 12px;
}
.banner-popup .banner-item {
  position: relative;
  border: 1px solid #e6e6f2;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #f8f8fc;
}
.banner-popup .banner-thumb {
  width: 100%;
  height: 110px;
  object-fit: cover;
  display: block;
}
.banner-popup .banner-delete {
  position: absolute;
  top: 6px;
  right: 6px;
  padding: 6px !important;
}

/* Keep the search box on the right on small screens */
@media (max-width: 576px) {
  .companies-page .companies-header { flex-direction: column; align-items: stretch; }
  .companies-page .companies-search { min-width: 0; width: 100%; }
}
</style>
